/**
 * Investigation pipeline types.
 * Defines the state machine, scope control, resource management,
 * and collection types for the Phase 2 AI investigation layer.
 *
 * Reference: ARCHITECTURE.md sections 5, 7
 */

// ---------------------------------------------------------------------------
// Investigation state machine
// ---------------------------------------------------------------------------

/** Pipeline states — ARCHITECTURE.md section 5.1 */
export type InvestigationState =
  | 'init'
  | 'decompose'
  | 'collect'
  | 'extract'
  | 'validate'
  | 'graph_build'
  | 'corroborate'
  | 'synthesize'
  | 'review'
  | 'error';

/** Lifecycle state — separate from pipeline state. Tracks analyst engagement. */
export type LifecycleState = 'review' | 'active' | 'idle' | 'closed' | 'archived';

/** Investigation tier — ARCHITECTURE.md section 5.5.4 */
export type InvestigationTierName = 'QUICK_SCAN' | 'STANDARD' | 'DEEP_DIVE';

export interface InvestigationTierConfig {
  readonly tokenBudget: number;
  readonly nodeCeiling: number;
  readonly edgeCeiling: number;
  readonly timeLimitSeconds: number;
  readonly sourceFilter: 'top_3_by_reliability' | 'all_enabled' | 'all_including_low_reliability';
  readonly relationshipMapping: boolean;
  readonly corroborationPasses: number;
  readonly synthesis: boolean;
  readonly sufficiencyThreshold: number;
  readonly secondOrderExpansion?: boolean;
  readonly temporalAnalysis?: boolean;
}

/** Tier resource limits — exact values from ARCHITECTURE.md section 5.5.4 */
export const INVESTIGATION_TIERS: Record<InvestigationTierName, InvestigationTierConfig> = {
  QUICK_SCAN: {
    tokenBudget: 50_000,
    nodeCeiling: 15,
    edgeCeiling: 25,
    timeLimitSeconds: 300,
    sourceFilter: 'top_3_by_reliability',
    relationshipMapping: false,
    corroborationPasses: 0,
    synthesis: false,
    sufficiencyThreshold: 0.5,
  },
  STANDARD: {
    tokenBudget: 200_000,
    nodeCeiling: 40,
    edgeCeiling: 60,
    timeLimitSeconds: 1800,
    sourceFilter: 'all_enabled',
    relationshipMapping: true,
    corroborationPasses: 1,
    synthesis: true,
    sufficiencyThreshold: 0.8,
  },
  DEEP_DIVE: {
    tokenBudget: 500_000,
    nodeCeiling: 80,
    edgeCeiling: 120,
    timeLimitSeconds: 3600,
    sourceFilter: 'all_including_low_reliability',
    relationshipMapping: true,
    corroborationPasses: 3,
    synthesis: true,
    sufficiencyThreshold: 0.85,
    secondOrderExpansion: true,
    temporalAnalysis: true,
  },
};

// ---------------------------------------------------------------------------
// Scope contract — ARCHITECTURE.md section 5.4.3
// ---------------------------------------------------------------------------

export interface ScopeSubject {
  type: string;
  subtype?: string;
  description: string;
  watchlistMatches: string[];
}

export interface BehaviorIndicator {
  type: string;
  threshold: string;
}

export interface GeographicBounds {
  source: string;
  bbox: [number, number, number, number]; // [south, west, north, east]
  override: boolean;
}

export interface TemporalBounds {
  start: string; // ISO 8601
  end: string;   // ISO 8601
  source: string;
}

export interface ScopeContract {
  subject: ScopeSubject;
  behaviorIndicators: BehaviorIndicator[];
  geographicBounds: GeographicBounds;
  temporalBounds: TemporalBounds;
  exclusions: string[];
}

// ---------------------------------------------------------------------------
// Investigation — full record
// ---------------------------------------------------------------------------

export interface Investigation {
  id: string;
  title: string;
  rawQuery: string;
  decomposed: ScopeContract | null;
  state: InvestigationState;
  deskId: string;
  tier: InvestigationTierName;
  scopeContract: ScopeContract | null;
  coverage: CoverageReport | null;
  tokenUsage: number;
  nodeCount: number;
  edgeCount: number;
  startedAt: string;
  completedAt: string | null;
  updatedAt: string;
  lifecycleState?: LifecycleState;
  lastActivityAt?: string;  // ISO 8601
}

// ---------------------------------------------------------------------------
// Collection tasks — ARCHITECTURE.md section 5.3
// ---------------------------------------------------------------------------

export type CollectionTaskStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'timeout'
  | 'rejected';

export interface CollectionTask {
  id: string;
  investigationId: string;
  sourceId: string;
  query: Record<string, unknown>;
  expectedEntityTypes: string[];
  status: CollectionTaskStatus;
  rawData: string | null;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
}

export interface CollectionPlan {
  investigationId: string;
  tasks: CollectionTask[];
  totalSources: number;
  estimatedTokens: number;
}

// ---------------------------------------------------------------------------
// Coverage tracking — ARCHITECTURE.md section 5.5.2
// ---------------------------------------------------------------------------

export interface CoverageParameter {
  name: string;
  addressed: boolean;
  corroborated: boolean;
  detail: string;
}

export interface CoverageReport {
  parameters: CoverageParameter[];
  overallPercentage: number;
  sufficientForSynthesis: boolean;
}

// ---------------------------------------------------------------------------
// Resource tracking — ARCHITECTURE.md section 5.5.6
// ---------------------------------------------------------------------------

export interface ResourceState {
  tokensUsed: number;
  tokenBudget: number;
  tokensPercent: number;
  nodeCount: number;
  nodeCeiling: number;
  edgeCount: number;
  edgeCeiling: number;
  elapsedSeconds: number;
  timeLimitSeconds: number;
  coveragePercent: number;
  infoGainTrend: number[]; // new-entity ratio per task, last N tasks
  diminishingReturns: boolean;
}

// ---------------------------------------------------------------------------
// Synthesis / Draft assessment
// ---------------------------------------------------------------------------

export interface DraftAssessment {
  investigationId: string;
  sections: AssessmentSection[];
  executiveSummary: string;
  generatedAt: string;
  modelUsed: string;
  tokenCost: number;
}

export interface AssessmentSection {
  title: string;
  content: string;
  citations: string[]; // entity IDs referenced
}

// ---------------------------------------------------------------------------
// Investigation log — audit trail
// ---------------------------------------------------------------------------

export type LogAction =
  | 'state_transition'
  | 'task_dispatched'
  | 'task_completed'
  | 'task_failed'
  | 'entity_extracted'
  | 'entity_rejected'
  | 'entity_merged'
  | 'validation_pass'
  | 'validation_fail'
  | 'llm_call'
  | 'resource_warning'
  | 'analyst_intervention'
  | 'synthesis_complete'
  | 'note_added'
  | 'error'
  // KAIROS-ready analyst actions
  | 'entity_dismissed'
  | 'synthesis_requested'
  | 'lifecycle_changed'
  | 'proposal_accepted'
  | 'proposal_rejected'
  | 'context_signal'
  | 'analyst_score';

export interface LogEntry {
  id: string;
  investigationId: string;
  timestamp: string;
  state: InvestigationState;
  action: LogAction;
  detail: Record<string, unknown>;
  agentId: string | null;
}
